import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

interface PageContext {
    title: string;
    url: string;
    id: string;
}

interface FeedbackModalProps {
    pageContext: PageContext;
    onClose: () => void;
}

type ReportMode = 'page' | 'email';
type EmailType = 'email_weekly' | 'email_monthly' | 'email_other';

const FeedbackModal: React.FC<FeedbackModalProps> = ({ pageContext, onClose }) => {
    const { user } = useAuth();
    const [reportMode, setReportMode] = useState<ReportMode>('page');
    const [emailType, setEmailType] = useState<EmailType>('email_weekly');
    const [formData, setFormData] = useState({
        first_name: '',
        email: '',
        feedback_text: '',
    });
    const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
    const [errorMessage, setErrorMessage] = useState('');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatus('submitting');
        setErrorMessage('');

        const report_type = reportMode === 'email' ? emailType : 'page';

        try {
            const response = await fetch('/api/feedback/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...formData,
                    report_type,
                    page_title: reportMode === 'page' ? pageContext.title : '',
                    page_url: reportMode === 'page' ? pageContext.url : '',
                    page_id: reportMode === 'page' ? pageContext.id : '',
                    user_type: user?.nickname || 'Guest',
                }),
            });

            if (response.ok) {
                setStatus('success');
                setFormData({ first_name: '', email: '', feedback_text: '' });
            } else {
                setStatus('error');
                setErrorMessage(`Server responded with ${response.status}`);
            }
        } catch (error: any) {
            console.error('Error submitting feedback:', error);
            setStatus('error');
            setErrorMessage(error.message || 'Unknown error occurred');
        }
    };

    const currentDateTime = new Date().toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });

    const textareaLabel = reportMode === 'email'
        ? 'Please describe the problem you experienced with this email.'
        : 'Please describe the problem you have experienced with this page.';

    return (
        <div
            className="fixed inset-0 bg-black/50 z-[80] flex items-center justify-center p-4"
            onClick={onClose}
        >
            <div
                className="bg-papyrus rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex justify-between items-center py-3 px-4 border-b-2 border-brand-maroon bg-brand-maroon sticky top-0 z-10">
                    <h2 className="text-xl font-bold text-white leading-none m-0">Report a Problem</h2>
                    <button
                        onClick={onClose}
                        className="text-white/70 hover:text-white transition-colors flex items-center"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">

                    {/* Report Type Toggle */}
                    <div className="flex rounded-lg overflow-hidden border border-gray-500">
                        <button
                            type="button"
                            onClick={() => setReportMode('page')}
                            className={`flex-1 py-2.5 px-4 text-sm font-semibold transition-colors ${reportMode === 'page'
                                ? 'bg-brand-maroon text-white'
                                : 'bg-white/30 text-gray-700 hover:bg-white/50'
                                }`}
                        >
                            🖥️ This Page
                        </button>
                        <button
                            type="button"
                            onClick={() => setReportMode('email')}
                            className={`flex-1 py-2.5 px-4 text-sm font-semibold transition-colors ${reportMode === 'email'
                                ? 'bg-brand-maroon text-white'
                                : 'bg-white/30 text-gray-700 hover:bg-white/50'
                                }`}
                        >
                            ✉️ An Email I Received
                        </button>
                    </div>

                    {/* F_Card_01 - Context Card (Page or Email) */}
                    <div className="card w-full !py-4">
                        {reportMode === 'page' ? (
                            <>
                                <h3 className="card-title text-lg">Page Information</h3>
                                <div className="p-4 rounded-lg bg-accent-bg space-y-2 text-sm">
                                    <div>
                                        <span className="font-semibold text-accent">Page Title:</span>{' '}
                                        <span className="text-text-primary">{pageContext.title}</span>
                                    </div>
                                    <div>
                                        <span className="font-semibold text-accent">Page URL:</span>{' '}
                                        <span className="text-text-primary">{pageContext.url}</span>
                                    </div>
                                    <div>
                                        <span className="font-semibold text-accent">Page ID:</span>{' '}
                                        <span className="text-text-primary">{pageContext.id}</span>
                                    </div>
                                    <div>
                                        <span className="font-semibold text-accent">Date &amp; Time:</span>{' '}
                                        <span className="text-text-primary">{currentDateTime}</span>
                                    </div>
                                    <div>
                                        <span className="font-semibold text-accent">User:</span>{' '}
                                        <span className="text-text-primary">{user?.nickname || 'Guest'}</span>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <h3 className="card-title text-lg">Email Information</h3>
                                <div className="p-4 rounded-lg bg-accent-bg space-y-4 text-sm">
                                    <p className="text-text-primary font-medium">Which email is this about?</p>
                                    <div className="space-y-2">
                                        {([
                                            ['email_weekly', 'Weekly Digest'],
                                            ['email_monthly', 'Monthly Digest'],
                                            ['email_other', 'Other Email'],
                                        ] as [EmailType, string][]).map(([value, label]) => (
                                            <label
                                                key={value}
                                                className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${emailType === value
                                                    ? 'bg-accent/15 border border-accent'
                                                    : 'bg-primary-bg/30 border border-border-color hover:bg-primary-bg/50'
                                                    }`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="email_type"
                                                    value={value}
                                                    checked={emailType === value}
                                                    onChange={() => setEmailType(value)}
                                                    className="accent-accent"
                                                />
                                                <span className="text-text-primary font-medium">{label}</span>
                                            </label>
                                        ))}
                                    </div>
                                    <div className="pt-2 border-t border-border-color space-y-2">
                                        <div>
                                            <span className="font-semibold text-accent">Date &amp; Time:</span>{' '}
                                            <span className="text-text-primary">{currentDateTime}</span>
                                        </div>
                                        <div>
                                            <span className="font-semibold text-accent">User:</span>{' '}
                                            <span className="text-text-primary">{user?.nickname || 'Guest'}</span>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* F_Card_02 - Feedback Form */}
                    <div className="card w-full !py-4">
                        <h3 className="card-title text-lg">Tell Us About the Problem</h3>
                        <div className="p-4">
                            {status === 'success' ? (
                                <div className="text-center py-10 space-y-4">
                                    <div className="text-green-400 font-bold text-lg">
                                        Thank you! Your report has been sent.
                                    </div>
                                    <button
                                        onClick={onClose}
                                        className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-primary-bg bg-accent hover:bg-accent/85 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent font-bold transition-colors duration-300"
                                    >
                                        Close
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div>
                                        <label htmlFor="first_name" className="block text-sm font-medium text-white mb-1">
                                            By what name should we address you?
                                        </label>
                                        <input
                                            type="text"
                                            id="first_name"
                                            name="first_name"
                                            required
                                            value={formData.first_name}
                                            onChange={handleChange}
                                            className="input-base mt-1 block w-full"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="email" className="block text-sm font-medium text-white mb-1">
                                            Email Address
                                        </label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            required
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="input-base mt-1 block w-full"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="feedback_text" className="block text-sm font-medium text-white mb-1">
                                            {textareaLabel}
                                        </label>
                                        <textarea
                                            id="feedback_text"
                                            name="feedback_text"
                                            rows={4}
                                            required
                                            value={formData.feedback_text}
                                            onChange={handleChange}
                                            className="input-base mt-1 block w-full"
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={status === 'submitting'}
                                        className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-primary-bg bg-accent hover:bg-accent/85 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50 font-bold transition-colors duration-300"
                                    >
                                        {status === 'submitting' ? 'Sending...' : 'Submit'}
                                    </button>
                                    {status === 'error' && (
                                        <div className="text-red-400 text-sm text-center">
                                            Something went wrong. {errorMessage}
                                        </div>
                                    )}
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FeedbackModal;
